import { autoinject, observable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, SortDirection, SortEntry, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthSearchFilters } from 'app/um/services/auth-service';
import { ITrackAuth } from 'app/um/services/tracking-service';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { QueueUserTracker, TrackingQueue } from 'app/um/components/tracking/queue-user-tracker';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { ExtendLocalState, applyLocalState } from 'app/shared/models/local-state-extensions';
import { AuthWithLocalState } from 'app/um/models/local-state-extensions';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { guid } from 'app/shared/lib/guid';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { MemberEligibility } from 'app/cd/models/essette-api';
import { IProviderSearchFilters } from 'app/cd/services/provider-service';
import { requiredFields as memberRequiredFields, requiredPlanFields as planRequiredFields } from 'app/cd/elements/members/member-display';
import { mergeObjects } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

interface IAuthOwnerLocalState { isLoaded?: boolean; }
type AuthOwnerWithLocalState = ExtendLocalState<UM.AuthOwner, IAuthOwnerLocalState>;

@autoinject()
export class PortalQueue extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected portalQueueId: string | null = null;
    // To load necessary portal queue information
    protected portalQueue: UM.PortalQueue | null = null;
    // Queue Auths to Display on Grid
    protected auths: Array<ITrackAuth> = [];
    protected selectedAuths: Array<AuthWithLocalState> = [];

    protected authSearchFilters: IAuthSearchFilters = {};
    protected providerSearchFilters: IProviderSearchFilters = {};
    protected authAvailableFilters = [
        'mine', 'openOnly', 'closedOnly', 'overDue', 'number', 'type', 'class', 'class.subclass', 'status', 'status.reason', 'assignedTo', 'requestedDateTime', 'dueDate', 'nextReviewDate'
    ];
    protected providerAvailableFilters = [
        'identifier', 'npi', 'providerFirstName', 'providerLastName', 'specialty', 'gender', 'facilityName', 'groupName', 'type', 'street1', 'city', 'region', 'postalCode'
    ];

    // Fields for Assigning Auth Owners
    protected owners: ReadonlyArray<AuthOwnerWithLocalState> = [];
    protected selectedUser: UM.AuthOwner | null = null;
    protected invalidCount: number = 0;
    private verifyState: number = 0;
    private verifiedAuths: AuthWithLocalState[] = [];

    // Fields for Tracking Users in Auths
    protected trackingQueue: TrackingQueue | null = null;
    @observable
    protected trackedAuths: ITrackAuth[] | null = null;

    // Fields for Grid
    private sorting = new Sorting();
    private defaultSortEntry?: SortEntry;
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    // Permission Access
    protected hasBulkAssignAuthsAccess: boolean = false;
    protected hasAuthRoute: boolean = false;
    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected hasAuthOwner: boolean = false;
    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $queueUserTracker!: QueueUserTracker;
    private $queueAuthsTable!: EssTableCustomElement<ITrackAuth>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly queue: TaskQueue,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(params: { portalQueueId: string }): Promise<boolean> {
        await this.accountContext.load();

        if(!params.portalQueueId) { throw new Error('Missing required parameters: portalQueueId'); }

        const portalQueueRouteCode: string = params.portalQueueId ? `um.queues.portal.${params.portalQueueId}` : '';
        if(!this.accountContext.hasRouteAccess(portalQueueRouteCode)) { return false; }

        this.hasAuthOwner = this.accountContext.hasPermission('UM.Auth.Duty.Owner');

        return true;
    }

    protected async activate(params: { portalQueueId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(!params?.portalQueueId) {
            throw new Error('Missing required parameters: portalQueueId');
        }

        this.portalQueueId = params.portalQueueId;

        // Set up Page Title
        await this.loadPortalQueueInformation();
        if(this.portalQueue && routeConfig.navModel) {
            const title = this.i18n.tr('um:routes.queue.portal.$title', { what: this.portalQueue.$display });
            routeConfig.navModel.setTitle(title);
        }

        // clear selections that might carry over from another queue
        this.clear();
        this.selectedUser = null;

        await Promise.all([
            this.checkPermissions(),
            this.loadSettings(),
            this.loadActiveAuthOwners(),
            this.reload()
        ]);
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;

        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        event.stopPropagation();
    }

    // New Tracked Auth changes, set the auth.users to the tracked auth users for the user-tracker icons.
    protected async trackedAuthsChanged(newValue: ITrackAuth[], oldValue: ITrackAuth[]): Promise<void> {
        if(newValue) {
            const trackedAuths = (newValue && newValue.length > 0) ? newValue : [];

            this.auths.forEach(a => {
                const trackedAuth = trackedAuths.find(ta => ta.id === a.id);
                if(trackedAuth) {
                    a.users = trackedAuth.users;
                }
                else {
                    a.users = [];
                }
            });
        }
    }

    // fired when a new user is selected
    protected assignmentChanged(event: Event) {
        // clear "isValid" flag for every auth since a new user has been selected
        applyLocalState(this.verifiedAuths, { isValid: null }, {});
        this.verifiedAuths = [];
        this.invalidCount = 0;
    }

    protected authsSelected({ detail }: SelectEvent<AuthWithLocalState>): void {
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.auths.forEach(auth => {
                    const index = this.selectedAuths.findIndex(
                        selected => selected.id === auth.id
                    );
                    if(index !== -1) {
                        this.selectedAuths.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedAuths.some(
                        service => service.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedAuths.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id in detail (allows dups and id's correct service)
            if(match) {
                // add to selectedServices if not already present
                const alreadySelected = this.selectedAuths.some(
                    auth => auth.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedAuths.push(detail.item!);
                }
            } else {
                // remove from selectedServcies if it exists based on the id of detail
                const index = this.selectedAuths.findIndex(
                    auth => auth.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedAuths.splice(index, 1);
                }
            }
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthsByPortalQueue();
    }

    private restoreSelected(): void {
        if(!(this.selectedAuths && this.selectedAuths.length > 0)) { return; }
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$queueAuthsTable.restoreSelected(this.selectedAuths);
        });
    }

    protected clear(): void {
        if(this.$queueAuthsTable) {
            this.$queueAuthsTable.deselectAll();
            this.selectedAuths.splice(0);
        }
    }

    private async checkPermissions(): Promise<void> {
        this.hasBulkAssignAuthsAccess = this.accountContext.hasPermission(`UM.Auth.Reassign.PortalQueueBulkReassign.um.queues.portal.${this.portalQueueId}`);
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected filterAuths(): void {
        this.clear();
        this.loadAuthsByPortalQueue();
    }

    protected clearResults(): void {
        this.clear();
        this.authSearchFilters = {};
        this.providerSearchFilters = {};
        this.loadAuthsByPortalQueue();
    }

    // Build track users queue to pass to queue-user-tracker
    private buildTrackUsersQueue(): void {
        if(this.portalQueueId) {
            this.trackingQueue = {
                id: this.portalQueueId,
                auths: this.auths,
                queueType: 'portal'
            }
        }
        else {
            this.trackingQueue = null
        }
    }

    private isVerifiedAuth(item: AuthWithLocalState): boolean {
        const result = (item._state && item._state.isValid !== undefined && item._state.isValid !== null) || false;
        return result;
    }

    private calculateInvalidCount(): void {
        const selected = this.selectedAuths;
        let count = 0;
        for(const selectedAuth of selected) {
            if(selectedAuth._state && selectedAuth._state.isValid === false) {
                count++;
            }
        }
        this.invalidCount = count;
    }

    // New auths loaded, synchronize "isValid" state from previously verified cases
    protected authsLoaded(): void {
        for(const item of this.auths) {
            const target = this.verifiedAuths.find(a => a.id === item.id);
            // if there is a selected item, copy the isValid value, otherwise, default to null
            let isValid =
                !target ? null : (target._state && target._state.isValid);
            applyLocalState([item as AuthWithLocalState], { isValid }, {});
        }
    }

    private getHealthPlan = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.healthPlan?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    private getLineOfBusiness = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.lineOfBusiness?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    private getProductList = (history: MemberEligibility[] | null | undefined): string => {
        if(!history) return '';
        return history
            .filter(x => x.product?.isActive)
            .map(x => x.product?.description ?? '')
            .filter(Boolean)
            .join(', ');
    };

    ////****************** Service Calls ********************************************////
    /** Loads active auth owners for the "Assign to" drop down */
    @apiCall
    private async loadActiveAuthOwners(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const fields = ['description', 'openAuthCount'];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadAuthOwners(options, undefined, undefined, undefined, fields);
            this.owners = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAuthsByPortalQueue(): Promise<void> {
        if(!this.portalQueueId) return;

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0 && this.defaultSortEntry) sort.entries.push(this.defaultSortEntry);
        else sort = this.sorting;

        const includes = [
            'class',
            'class.subClass',
            'healthcareServiceType',
            'placeOfService',
            'priority',
            'provider.attending',
            'provider.attending.location.provider',
            'provider.requested',
            'provider.requested.location.provider',
            'provider.requesting',
            'provider.requesting.location.provider',
            'provider.servicing',
            'provider.servicing.location.provider',
            'type'
        ];

        const memberIncludes = [
            'member',
            'member.plan',
            'member.plan.eligibility.history.product',
            'member.plan.eligibility.history.product.healthPlan',
            'member.plan.eligibility.history.product.lineOfBusiness',
        ];

        const fields = [
            'class.description',
            'class.subClass.description',
            'dueDateTime',
            'healthcareServiceType.description',
            'isClosed',
            'number',
            'placeOfService.description',
            'priority.description',
            'provider.attending.location.provider.description',
            'provider.attending.location.provider.name.full',
            'provider.requested.location.provider.description',
            'provider.requested.location.provider.name.full',
            'provider.requesting.location.provider.description',
            'provider.requesting.location.provider.name.full',
            'provider.servicing.location.provider.description',
            'provider.servicing.location.provider.name.full',
            'requestedDateTime',
            'type.description'
        ];

        const memberFields = [
            'member.identifier.medicaid',
            'member.birthDate',
            'member.plan.eligibility.isCurrent',
            'member.plan.eligibility.history.product.description',
            'member.plan.eligibility.history.product.healthPlan.description',
            'member.plan.eligibility.history.product.isActive',
            'member.plan.eligibility.history.product.lineOfBusiness.description',
        ];

        memberRequiredFields.forEach(field => !memberFields.includes(`member.${field}`) ? memberFields.push(`member.${field}`) : null);
        planRequiredFields.forEach(field => !memberFields.includes(`member.plan.${field}`) ? memberFields.push(`member.plan.${field}`) : null);

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: sort
        };


        try {
            this.state.beginLoad();

            // too many filters can exceed URL character limit, so use filters only to fetch IDs first
            const idResponse = await this.authService.searchAuthQueues(this.portalQueueId, this.authSearchFilters, this.providerSearchFilters, options, true);
            const authIds: string[] = idResponse.results.map(auth => auth.id!);
            // then use those IDs to fetch the rest of the includes and fields
            const [fullResponse, fullMemberResponse] = await Promise.all([
                this.authService.loadAuthsByIdFromAuthQueue(this.portalQueueId, authIds, { includeInactive: true, sorting: options.sorting }, includes, fields, true),
                this.authService.loadAuthsByIdFromAuthQueue(this.portalQueueId, authIds, { includeInactive: true, sorting: options.sorting }, memberIncludes, memberFields, true)
            ]);

            // consolidate results
            for(const auth of fullResponse.results) {
                const foundMember = fullMemberResponse.results.find(a => a.id === auth.id)?.member;
                if(foundMember) auth.member = foundMember;
            }

            this.auths = fullResponse.results;
            this.resultCount = idResponse.count || 0;
            this.buildTrackUsersQueue();
            this.authsLoaded();
            this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPortalQueueInformation(): Promise<void> {
        if(!this.portalQueueId) return;
        try {
            this.state.beginLoad();
            const fields = ['description'];
            const response = await this.authService.loadPortalQueue(this.portalQueueId, undefined, fields);
            this.portalQueue = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async verifyAssignment(): Promise<void> {
        try {
            this.state.beginProcessing();

            // keep track of most recent verification attempt
            const previousState = ++this.verifyState;

            // get selected auths except for ones that have already been verified
            const selected = this.selectedAuths.filter(a => !this.isVerifiedAuth(a));
            const auths = getReferences(selected);

            if(!this.selectedUser || !this.selectedUser.id || !auths || !auths.length) {
                this.invalidCount = 0;
                return;
            }

            const response = await this.authService.verifyAssignment(this.selectedUser.id, auths);

            if(previousState !== this.verifyState) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                // only stash results if another newer request has not been made
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // set all 'isValid' to false
                    applyLocalState(selected, { isValid: false }, {});
                    for(const item of response.result.filter.items) {
                        const found = selected.find(a => a.id === item.id);
                        if(found) {
                            // switch 'isValid' to true
                            applyLocalState([found], { isValid: true }, {});
                        }
                    }
                    // keep a list of all verified auths so we don't check them again
                    this.verifiedAuths.push(...selected);
                }
            }
        }
        finally {
            this.state.endProcessing();
            this.calculateInvalidCount();
        }
    }

    @apiCall
    protected async assignAuths(): Promise<void> {
        if(!this.selectedUser?.id) {
            this.logger.error('Selected user is not set!');
            this.toaster.error().message('Selected User not set!').show();
            return;
        }

        // make sure verification has finished running
        await this.verifyAssignment();
        if(this.invalidCount > 0) return;

        const promises = new Array<Promise<IApiActionResults>>();
        this.selectedAuths
            .map(a => {
                return {
                    id: a.id,
                    assignedTo: getReference(this.selectedUser),
                    removeFromPortalQueue: true
                } as UM.Auth
            })
            .forEach(a => {
                promises.push(this.authService.assignAuth(a));
            });

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => r.isSuccessful);
            this.reload();
            this.loadActiveAuthOwners();
            this.clear();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.assigned('um:auth.$', isSuccessful, promises.length);
            this.state.endSave();
        }
    }

    @apiCall
    private async goToAuthAndTake(auth: UM.Auth): Promise<void> {
        // only take auth if user is not already assigned to auth, and user has auth owner permission
        const takeAuth: boolean = (auth.assignedTo?.id !== this.env.user.id) && this.hasAuthOwner;
        let isSuccessful = false;
        try {
            // if user is taking auth, do so before navigating to route
            if(takeAuth) {
                this.state.beginSave();
                const saveAuth: UM.Auth = {
                    id: auth.id,
                    assignedTo: getReference(this.env.user),
                    removeFromPortalQueue: true
                };
                const response = await this.authService.assignAuth(saveAuth);
                isSuccessful = response.isSuccessful;
            }
            this.router.navigateToRoute('um.auth', { authId: auth.id, queueId: this.portalQueueId, queueTypeCode: 'UM.Portal' });
        } catch(error) {
            throw error;
        } finally {
            if(takeAuth) {
                this.toaster.standard.assigned('um:auth.$', isSuccessful);
                this.state.endSave();
            }
        }
    }

    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.authService.loadAuthSettings();
            const settings = response.settings;

            // attempt to configure default sort via attribute
            const defaultSort = getApiSetting(settings, 'defaultPortalQueueSort.$default')?.toString();
            if(!defaultSort) return; // no attribute value found

            const [reference, directionString]: string[] = defaultSort.split(':');
            if(!reference || !['asc', 'desc'].includes(directionString)) return; // attribute value is invalid

            this.defaultSortEntry = {
                reference,
                direction: directionString === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            };
        } catch(error) {
            throw error;
        } finally {
            // if default sort not configured via attribute, use fallback
            if(!this.defaultSortEntry) {
                this.defaultSortEntry = {
                    reference: 'dueDateTime',
                    direction: SortDirection.Ascending
                };
            }
            this.state.endLoad();
        }
    }
}
